import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Box, Button, Stack, Typography } from '@mui/material';
import { ResponsiveSideDialog } from 'common/components/ResponsiveSideDialog/ResponsiveSideDialog';
import { FormInput } from 'common/form/components/FormInput';
import { FormSelect } from 'common/form/components/FormSelect';
import { phoneValidator, transformNaNtoNull } from 'common/form/validators';
import { DialogComponentProps, useDialog } from 'common/hooks/useDialog';
import { ContentDispositionType } from 'common/hooks/useFiles';
import { useFilesDownload } from 'common/hooks/useFilesDownload';
import { useNotification } from 'common/hooks/useNotification';
import { AuthContext } from 'common/providers/AuthContextProvider/AuthContextProvider';
import { normalizePhoneNumber } from 'common/utils/formatUtils';
import { getYear } from 'date-fns';
import {
  ReportType,
  ServiceLocation,
  SortDirection,
  useClientProfileQuery,
  useCreateReportMutation,
  useServiceLocationsQuery,
  useUpdateClientProfileMutation,
} from 'generated/graphql';
import { useContext, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { FinoptioReportIntroDialog } from '../FinoptioReportIntroDialog/FinoptioReportIntroDialog';

interface FormValues {
  name?: string;
  surname?: string;
  birthYear?: number;
  preferredServiceLocation?: { id: string | null };
  // FormSelect doesn't accept boolean values so we convert it to number and back
  partner?: { id: number | null };
  partnerBirthYear?: number | null;
  childrenNumber?: number;
  youngestChildBirthYear?: number | null;
  phoneNumber?: string;
}

const FinoptioReportClientProfileDialogContent: React.FC<DialogComponentProps<void>> = ({ onResolve }) => {
  const { t } = useTranslation();
  const notify = useNotification();
  const { isAuthenticated, updateAttributes, userData } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);

  const { data: serviceLocations } = useServiceLocationsQuery({
    skip: !isAuthenticated,
    variables: {
      sortInput: {
        by: [{ direction: SortDirection.Asc, fieldPath: 'name' }],
      },
    },
  });

  const { data: clientProfile } = useClientProfileQuery({
    skip: !isAuthenticated,
  });

  const [updateClientProfile] = useUpdateClientProfileMutation({});
  const [createReport] = useCreateReportMutation({});

  const currentYear = getYear(new Date());

  const schema = yup.object({
    birthYear: yup
      .number()
      .transform(transformNaNtoNull)
      .nullable(true)
      .integer(t('form.validator.integer'))
      .min(currentYear - 100, t('form.validator.min', { value: currentYear - 100 }))
      .max(currentYear - 18, t('form.validator.max', { value: currentYear - 18 }))
      .required(t('form.validator.fieldIsRequired')),

    childrenNumber: yup
      .number()
      .transform(transformNaNtoNull)
      .nullable(true)
      .integer(t('form.validator.integer'))
      .min(0, t('form.validator.min', { value: 0 }))
      .required(t('form.validator.fieldIsRequired')),

    name: yup.string().required(t('form.validator.fieldIsRequired')),

    partner: yup.mixed().required(t('form.validator.selectionIsRequired')),

    partnerBirthYear: yup.number().when('partner', {
      is: (value?: { id: number | null }) => value && value.id,
      otherwise: yup.number().transform(transformNaNtoNull).nullable(true),
      then: yup
        .number()
        .transform(transformNaNtoNull)
        .nullable(true)
        .integer(t('form.validator.integer'))
        .min(currentYear - 100, t('form.validator.min', { value: currentYear - 100 }))
        .max(currentYear, t('form.validator.max', { value: currentYear }))
        .required(t('form.validator.fieldIsRequired')),
    }),

    phoneNumber: phoneValidator().required(t('form.validator.fieldIsRequired')),

    preferredServiceLocation: yup.mixed().required(t('form.validator.selectionIsRequired')),

    surname: yup.string().required(t('form.validator.fieldIsRequired')),

    youngestChildBirthYear: yup.number().when('childrenNumber', {
      is: (value?: number) => value && value > 0,
      otherwise: yup.number().transform(transformNaNtoNull).nullable(true),
      then: yup
        .number()
        .transform(transformNaNtoNull)
        .nullable(true)
        .integer(t('form.validator.integer'))
        .min(currentYear - 100, t('form.validator.min', { value: currentYear - 100 }))
        .max(currentYear, t('form.validator.max', { value: currentYear }))
        .required(t('form.validator.fieldIsRequired')),
    }),
  });

  const formMethods = useForm<FormValues>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (userData?.user.attributes && clientProfile?.clientProfile && !formMethods.formState.isDirty) {
      formMethods.reset({
        birthYear: clientProfile?.clientProfile.birthYear,
        childrenNumber: clientProfile?.clientProfile.childrenNumber,
        name: userData.user.attributes.given_name,
        partner: {
          id: Number(clientProfile?.clientProfile.partner),
        },
        partnerBirthYear: clientProfile?.clientProfile.partnerBirthYear,
        phoneNumber: userData.user.attributes.phone_number,
        preferredServiceLocation: {
          id: clientProfile?.clientProfile.preferredServiceLocation.id,
        },
        surname: userData.user.attributes.family_name,
        youngestChildBirthYear: clientProfile?.clientProfile.youngestChildBirthYear,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData, clientProfile]);

  const { downloadFile } = useFilesDownload();

  const handleSubmit = formMethods.handleSubmit(async (data) => {
    setLoading(true);

    const normalizedPhoneNumber = normalizePhoneNumber(data.phoneNumber ?? '');
    const resp = await updateAttributes({
      family_name: data.surname ?? '',
      given_name: data.name ?? '',
      phone_number: normalizedPhoneNumber,
    });

    if (resp) {
      notify({ message: t('common:somethingWentWrong'), type: 'error' });
      setLoading(false);
    } else {
      await updateClientProfile({
        onError: () => {
          notify({ message: t('common:somethingWentWrong'), type: 'error' });
          setLoading(false);
        },
        variables: {
          clientProfile: {
            birthYear: data.birthYear ?? 0,
            childrenNumber: data.childrenNumber ?? 0,
            partner: Boolean(data.partner?.id),
            partnerBirthYear: data.partnerBirthYear,
            preferredServiceLocationId: data.preferredServiceLocation?.id ?? '',
            youngestChildBirthYear: data.youngestChildBirthYear,
          },
        },
      });
      const resp = await createReport({
        onError: () => {
          notify({ message: t('common:somethingWentWrong'), type: 'error' });
          setLoading(false);
        },
        refetchQueries: ['Reports', 'ContactUserRelation'],
        variables: {
          reportType: ReportType.Finoptio,
        },
      });
      if (resp.data?.createReport.file !== undefined) {
        setLoading(false);
        await downloadFile(resp.data?.createReport.file, ContentDispositionType.ATTACHMENT, window.open());
        onResolve();
      } else {
        notify({ message: t('common:somethingWentWrong'), type: 'error' });
        setLoading(false);
      }
    }
  });

  const finoptioReportIntroDialog = useDialog<DialogComponentProps<void>, void>(FinoptioReportIntroDialog, {
    instanceId: 'finoptio-report-intro-dialog',
    navigable: true,
  });

  const handleBack = async () => {
    await onResolve();
    finoptioReportIntroDialog.create({});
  };

  return (
    <Box>
      <Stack alignItems="center" spacing={1}>
        <Typography color="textSecondary" variant="bodyM">
          {t('finoptioReport.clientProfileDialog.step')}
        </Typography>
        <Typography textAlign="center" variant="headlineXL">
          {t('finoptioReport.clientProfileDialog.title')}
        </Typography>
        <Typography textAlign="center" variant="bodyL">
          {t('finoptioReport.clientProfileDialog.text')}
        </Typography>
      </Stack>

      <Stack alignItems="center" mt={4} spacing={4}>
        <FormProvider {...formMethods}>
          <FormInput
            autoComplete="name"
            fullWidth
            label={t('finoptioReport.clientProfileDialog.inputs.name.label')}
            name="name"
            type="text"
          />
          <FormInput
            autoComplete="surname"
            fullWidth
            label={t('finoptioReport.clientProfileDialog.inputs.surname.label')}
            name="surname"
            type="text"
          />
          <FormSelect
            label={t('finoptioReport.clientProfileDialog.inputs.preferredServiceLocation.label')}
            name="preferredServiceLocation"
            options={
              serviceLocations?.serviceLocations.items.map((item: ServiceLocation) => ({
                label: item.name,
                value: {
                  id: item.id,
                },
              })) ?? []
            }
            size="large"
          />
          <FormInput
            autoComplete="birthYear"
            fullWidth
            label={t('finoptioReport.clientProfileDialog.inputs.birthYear.label')}
            name="birthYear"
            placeholder="YYYY"
            type="number"
          />
          <FormSelect
            label={t('finoptioReport.clientProfileDialog.inputs.partner.label')}
            name="partner"
            options={[
              { label: t('common:boolean.true'), value: { id: 1 } },
              { label: t('common:boolean.false'), value: { id: 0 } },
            ]}
            size="large"
          />
          <FormInput
            autoComplete="partnerBirthYear"
            fullWidth
            label={t('finoptioReport.clientProfileDialog.inputs.partnerBirthYear.label')}
            name="partnerBirthYear"
            placeholder="YYYY"
            type="number"
          />
          <FormInput
            autoComplete="childrenNumber"
            fullWidth
            label={t('finoptioReport.clientProfileDialog.inputs.childrenNumber.label')}
            name="childrenNumber"
            type="number"
          />
          <FormInput
            autoComplete="youngestChildBirthYear"
            fullWidth
            label={t('finoptioReport.clientProfileDialog.inputs.youngestChildBirthYear.label')}
            name="youngestChildBirthYear"
            placeholder="YYYY"
            type="number"
          />
          <FormInput
            autoComplete="phoneNumber"
            fullWidth
            label={t('finoptioReport.clientProfileDialog.inputs.phoneNumber.label')}
            name="phoneNumber"
            placeholder={t('finoptioReport.clientProfileDialog.inputs.phoneNumber.placeholder')}
            type="tel"
          />
        </FormProvider>
      </Stack>

      <Stack mt={5} spacing={1}>
        <LoadingButton color="primary" fullWidth loading={loading} onClick={handleSubmit} size="large">
          <Typography color="white" variant="bodyL">
            {t('finoptioReport.clientProfileDialog.submit')}
          </Typography>
        </LoadingButton>
        <Button color="tertiaryButton" fullWidth onClick={handleBack} size="large">
          <Typography color="white" variant="bodyL">
            {t('common:back')}
          </Typography>
        </Button>
      </Stack>
    </Box>
  );
};

export const FinoptioReportClientProfileDialog: React.FC<DialogComponentProps<void>> = (props) => {
  return (
    <ResponsiveSideDialog dialogId={props.instanceId} isOpen={props.isOpen} onClose={props.onResolve}>
      <FinoptioReportClientProfileDialogContent {...props} />
    </ResponsiveSideDialog>
  );
};
